/**
 * CapacityOverviewCascade — cascade « du temps payé à la capacité inutilisée ».
 * Vue connectée (lecture seule) : structure ← standard %TO, usage ← mensuel réel,
 * DEUX colonnes depuis le 2026-09-01 : N-1 et N, l'une et l'autre sur l'année
 * entière, lues à la même source que les onglets voisins (Capacité pratique,
 * Heures affectées). La colonne « extrapolé » reconstituait une année à partir
 * des seuls mois saisis au mensuel ; ces heures se saisissent désormais à
 * l'année, elle n'avait plus rien à reconstituer.
 * Charte Palestre uniquement (variables :root).
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../i18n/useP';
import { getCapacityOverview } from '../../api/capacityOverview';
import './CapacityOverviewCascade.css';

const fmtH = (v) => (v == null ? '—' : String(Math.round(v)).replace(/\B(?=(\d{3})+(?!\d))/g, '’') + ' h');
const fmtN = (v) => (v == null ? '—' : (Math.round(v * 10) / 10).toString());
const fmtP = (v) => (v == null ? '' : Math.round(v) + ' %');

export default function CapacityOverviewCascade({ tenantId, year, addToast }) {
  const { t } = useTranslation();
  const p = useP();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tenantId) { setData(null); return; }
    setLoading(true);
    getCapacityOverview(tenantId, year)
      .then(r => setData((r && r.data) || r || null))
      .catch(e => addToast && addToast(e?.response?.data?.detail || p('Échec du chargement de la vue d’ensemble'), 'error'))
      .finally(() => setLoading(false));
  }, [tenantId, year]); // eslint-disable-line

  if (loading) return <p className="page-table-loading">{p('Chargement…')}</p>;
  if (!data) return null;
  const { prev, real } = data.columns;

  // Ligne de cascade : clé, libellé, indent, type (input/sub/tot), couleur Nature.
  const ROWS = [
    { k: 'eff_moyen', label: p('Effectif moyen'), kind: 'inp', num: true, nat: p('moyen mensuel (contrats)') },
    { k: 'ept_moyen', label: p('EPT moyen'), kind: 'inp', num: true, nat: p('équivalents plein temps') },
    { k: 'cap_fte', label: t('phrases:p_75c6ea1166'), kind: 'inp', num: true, nat: p('capacité disponible (TR) en EPT') },
    /* La cascade part du TEMPS TOTAL, pas du temps ouvert (2026-08-30) : elle
       s'annonce « du temps payé à la capacité inutilisée », or le TO a déjà
       perdu les vacances. Les deux premières lignes rendent la déduction
       visible, comme la tuile « Point de départ ». */
    /* « ↳ » et non « = » (02.09.2026) : ces lignes ne posent pas une égalité
       entre deux grandeurs, elles disent ce qui RÉSULTE des lignes du dessus. */
    { k: 'tt', label: p('Temps total (TT)'), kind: 'sub', nat: p('temps payé total') },
    { k: 'holidays', label: p('− Vacances & congés'), ind: true, nat: p('payé non dispo · contrats') },
    { k: 'to', label: p('↳ Temps ouvert (TO)'), kind: 'sub', nat: p('temps disponible') },
    { k: 'corp', label: p('− Gestion'), ind: true, nat: p('structure · carve · effectif') },
    { k: 'sup', label: p('− Supervision'), ind: true, nat: p('structure · carve · effectif') },
    { k: 'special', label: t('phrases:p_1c3958a7af'), ind: true, nat: p('payé non dispo · carve · effectif') },
    { k: 'tr', label: p('↳ Capacité disponible (TR)'), kind: 'sub', nat: p('temps productif disponible') },
    /* Mêmes noms que dans « Heures affectées » (2026-09-01) : une grandeur
       porte UN nom, sinon on croit lire deux choses. */
    { k: 'billable', label: p('− Heures TF'), ind: true, natColor: 'sage', nat: p('vendu · saisi') },
    { k: 'nonbill', label: p('− NF client'), ind: true, natColor: 'amber', nat: p('interne · saisi') },
    { k: 'project', label: p('− Temps projets'), ind: true, natColor: 'amber', nat: p('interne · saisi') },
    { k: 'absence', label: t('phrases:p_db91a6d5f7'), ind: true, nat: p('maladie · saisi') },
    /* Le solde a DEUX sens et DEUX lignes : ce qui reste de capacité, et ce
       qu'on a affecté au-delà d'elle. Les deux ne se compensent pas. */
    { k: '_over', label: p('↳ Sur-production'), ind: true, bold: true, natColor: 'terra', nat: p('affecté au-delà de la capacité') },
    { k: '_vacant', label: p('↳ Capacité non utilisée'), ind: true, bold: true, nat: p('présent, sans affectation') },
  ];

  /* Les deux soldes se DÉDUISENT de l'inutilisé : positif il est vacant,
     négatif il est de la sur-production. Calculés ici, pas au serveur : c'est
     la même grandeur lue par ses deux bouts. */
  const val = (col, k) => (k === '_vacant' ? Math.max(0, col.idle)
    : k === '_over' ? Math.max(0, -col.idle)
      : col[k]);
  const cell = (col, r) => {
    if (r.num) return fmtN(val(col, r.k));
    return fmtH(val(col, r.k));
  };
  /* Le 100 % de l'échelle est le TEMPS OUVERT : c'est la base des carves et du
     TR. Le TT et les vacances vivent AVANT cette base, ils n'ont pas de part. */
  const share = (col, k, base) => {
    if (k === '_vacant' || k === '_over') {
      const v = val(col, k);
      return base ? Math.round(v / base * 1000) / 10 : null;
    }
    return null;
  };
  const pctTO = (col, r) => {
    if (r.num || ['tt', 'holidays'].includes(r.k)) return '';
    if (r.k === 'to') return '100 %';
    if (r.k === '_vacant' || r.k === '_over') return fmtP(share(col, r.k, col.to));
    return fmtP(col.pct_to?.[r.k]);
  };
  const pctTR = (col, r) => {
    if (r.num || ['to', 'tt', 'holidays'].includes(r.k)) return '';
    if (r.k === 'tr') return '100 %';
    if (['corp', 'sup', 'special'].includes(r.k)) return '';
    if (r.k === '_vacant' || r.k === '_over') return fmtP(share(col, r.k, col.tr));
    return fmtP(col.pct_tr?.[r.k]);
  };

  return (
    <div className="cao">
      <div className="cao-card">
        <div className="cao-head">{p('Capacité — du temps payé à la capacité inutilisée (N-1 / N)')}</div>
        <div className="cao-table-wrap">
          <table className="cao-table">
            <thead>
              <tr>
                <th>&nbsp;</th>
                <th>{prev.label}</th><th className="pct">% TO</th><th className="pct">% TR</th>
                <th className="rc rc-first">{real.label}</th><th className="pct rc">% TO</th><th className="pct rc">% TR</th>
                <th className="nat">{p('Nature')}</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map(r => (
                <tr key={r.k} className={`${r.kind === 'sub' ? 'sub' : r.kind === 'tot' ? 'tot' : r.kind === 'inp' ? 'inp' : ''}${r.bold ? ' idle-row' : ''}`}>
                  <td className={r.ind ? 'ind' : 'lbl'}>{r.label}</td>
                  <td>{cell(prev, r)}</td><td className="pct">{pctTO(prev, r)}</td><td className="pct">{pctTR(prev, r)}</td>
                  <td className="rc rc-first">{cell(real, r)}</td><td className="pct rc">{pctTO(real, r)}</td><td className="pct rc">{pctTR(real, r)}</td>
                  <td className={`nat${r.natColor ? ' nat--' + r.natColor : ''}`}>{r.nat}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!data.calendar_proration && (
          <div className="cao-note">{t('phrases:p_9f323a9088', { year })}</div>
        )}
      </div>
    </div>
  );
}
